import { Box, Paper, Skeleton, Typography } from '@mui/material';
import type { EChartsOption } from 'echarts';

import { PALETTE } from 'entities/analytics/lib';
import { useActiveUsers } from 'entities/analytics/model';
import { EChart } from 'entities/analytics/ui/echart';

const StatCard = ({
  label,
  value,
  caption,
  hero,
}: {
  label: string;
  value: string;
  caption?: string;
  hero?: boolean;
}) => (
  <Paper
    sx={{
      p: 2,
      height: '100%',
      ...(hero && { background: `linear-gradient(135deg, ${PALETTE.primary}, ${PALETTE.primaryDark})`, color: '#fff' }),
    }}
  >
    <Typography variant="caption" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, opacity: hero ? 0.85 : 0.7 }}>
      {label}
    </Typography>
    <Typography variant="h4" fontWeight={600} lineHeight={1.2}>
      {value}
    </Typography>
    {caption && (
      <Typography variant="caption" sx={{ opacity: hero ? 0.85 : 0.7 }}>
        {caption}
      </Typography>
    )}
  </Paper>
);

const gaugeOption = (ratio: number): EChartsOption => ({
  series: [
    {
      type: 'gauge',
      startAngle: 200,
      endAngle: -20,
      min: 0,
      max: 0.5,
      radius: '95%',
      center: ['50%', '58%'],
      progress: { show: true, width: 9, itemStyle: { color: PALETTE.primary } },
      axisLine: { lineStyle: { width: 9, color: [[1, PALETTE.grid]] } },
      pointer: { show: false },
      axisTick: { show: false },
      splitLine: { show: false },
      axisLabel: { show: false },
      anchor: { show: false },
      title: { show: false },
      detail: {
        fontSize: 24,
        fontWeight: 600,
        offsetCenter: [0, '-4%'],
        color: PALETTE.ink,
        formatter: (v: number) => v.toFixed(2),
      },
      data: [{ value: ratio }],
    },
  ],
});

export const KpiStrip = () => {
  const { data, isLoading } = useActiveUsers();
  const d = data?.data;

  if (isLoading) {
    return (
      <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(5, 1fr)' }, mb: 2 }}>
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} variant="rectangular" height={120} sx={{ borderRadius: 1 }} />
        ))}
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(5, 1fr)' }, mb: 2 }}>
      <StatCard hero label="Total users" value={(d?.totalUsers ?? 0).toLocaleString()} caption="All accounts" />

      <StatCard label="MAU" value={(d?.mau ?? 0).toLocaleString()} caption="lastLogin ≤ 30d" />
      <StatCard label="WAU" value={(d?.wau ?? 0).toLocaleString()} caption="lastLogin ≤ 7d" />
      <StatCard label="DAU" value={(d?.dau ?? 0).toLocaleString()} caption="lastLogin ≤ 24h" />
      <Paper sx={{ p: 2, height: '100%' }}>
        <Typography variant="caption" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, opacity: 0.7 }}>
          Stickiness · DAU/MAU
        </Typography>
        {d?.dauMauRatio == null ? (
          <Typography variant="h4" fontWeight={600}>
            —
          </Typography>
        ) : (
          <EChart option={gaugeOption(d.dauMauRatio)} height={96} />
        )}
      </Paper>
    </Box>
  );
};
